import { useRef, useState } from 'react'

import { Loader } from '@googlemaps/js-api-loader'

import type {
  PartnerCardProps,
  PartnerCategories,
} from '~/components/directory/PartnerCard/PartnerCard'
import { partnerCard } from '~/schemas/common'
import { useQuery } from '~/services/query'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import scrollToWithCallback from '~/utils/scrollToWithCallback'

export const useDirectory = (partners: PartnerCardProps[]) => {
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>()
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [nearMeActive, setNearMeActive] = useState<boolean>(false)
  const [showLocationDrawer, setShowLocationDrawer] = useState<boolean>(false)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const resultsRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [searchRadius, setSearchRadius] = useState<number>(100)
  const [usersLocation, setUsersLocation] = useState<
    | {
        lat: number
        lng: number
      }
    | undefined
  >()

  const pageSize = 10
  const resultsOffsetTop = resultsRef.current?.offsetTop
    ? resultsRef.current?.offsetTop
    : 0
  const listOffsetTop = listRef.current?.offsetTop
    ? listRef.current?.offsetTop
    : 0

  const scrollToMap = () => {
    scrollToWithCallback(resultsOffsetTop - 48, () => false)
  }

  const categoryClickHandler = async (category: PartnerCategories) => {
    setPageNumber(1)
    if (selectedFilter === category.slug) {
      setSelectedFilter(undefined)
      return
    }

    scrollToMap()

    await multiPostHogEventPusher([
      {
        eventType: `Partners ${category.name} category filter clicked`,
      },
    ])

    setSelectedFilter(category.slug)
  }

  const nearMeClickHandler = () => {
    if (!nearMeActive) {
      setPageNumber(1)
      setNearMeActive(true)

      const successCallback = async (position: any) => {
        setSearchRadius(100)
        const { latitude, longitude } = position.coords
        setUsersLocation({ lat: latitude, lng: longitude })
        if (selectedFilter === 'near-me') {
          setSelectedFilter(undefined)
          return
        }

        scrollToMap()

        await multiPostHogEventPusher([
          {
            eventType: 'Partners near me filter clicked',
          },
        ])
      }

      const errorCallback = (error: any) => {
        if (error.code === error.PERMISSION_DENIED) {
          setNearMeActive(false)
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setNearMeActive(false)
        } else if (error.code === error.TIMEOUT) {
          setNearMeActive(false)
        } else {
          setNearMeActive(false)
        }
      }

      navigator.geolocation.getCurrentPosition(successCallback, errorCallback)
    } else {
      setNearMeActive(false)
    }
  }

  const mapViewHandler = () => {
    scrollToMap()
  }

  const listViewHandler = () => {
    scrollToWithCallback(listOffsetTop, () => false)
  }

  const locationClickHandler = (open = true) => {
    setShowLocationDrawer(open)
  }

  const cancelLocationClickHandler = () => {
    setSelectedLocation(null)
    setNearMeActive(false)
    setUsersLocation(undefined)
    setSelectedFilter(undefined)
    setShowLocationDrawer(false)
    setSearchRadius(100)
    setPageNumber(1)
  }

  const locationChangeHandler = (
    location: string,
    isPostcode?: boolean,
    range?: number
  ) => {
    const loader = new Loader({
      apiKey: process.env.NEXT_PUBLIC_MAPS_API_KEY
        ? process.env.NEXT_PUBLIC_MAPS_API_KEY
        : '',
      version: 'weekly',
    })

    loader
      .importLibrary('geocoding')
      .then(google => {
        const geocoder = new google.Geocoder()

        geocoder.geocode(
          { address: location },
          function (results: any, status: any) {
            if (status === 'OK' && results !== null) {
              const { lat, lng } = results[0].geometry.location

              setUsersLocation({ lat: lat(), lng: lng() })
            }

            setSearchRadius(
              range !== undefined ? (range === 0 ? 1 : range) : 1000
            )
            setShowLocationDrawer(false)
            setSelectedLocation(location)
            scrollToMap()
          }
        )
      })
      .catch(e => {
        console.log(e)
      })
  }

  let filters = ''

  if (selectedFilter) {
    filters = `${filters} && '${selectedFilter}'  in category[]->slug.current`
  }
  if (nearMeActive || selectedLocation) {
    filters = `${filters} && geo::distance(geoLocation, geo::latLng(${usersLocation?.lat}, ${usersLocation?.lng})) < ${searchRadius} * 1000`
  }

  // Add a sorting mechanism based on 'type'
  const orderClause = `| order(
  select(
    type == 'accredited' => 1,
    type == 'sponsored' => 2,
    type == 'trusted' => 3,
    type == null => 4,
    5 // default case for any unexpected type values
  ) asc
)`

  const query = `*[_type == 'partner' ${filters}]${orderClause}${partnerCard}`

  const { data: dynamicPartners, isLoading, isError } = useQuery(query)

  const loadMoreHandler = () => {
    // As we already load the entire list of locations to display them on the map. Load more simple chunks the results for the lists.
    setPageNumber(pageNumber + 1)
  }

  return {
    categoryClickHandler,
    nearMeClickHandler,
    selectedFilter,
    dynamicPartners: dynamicPartners ? dynamicPartners : partners,
    chunkedPartners: dynamicPartners?.slice(0, pageNumber * pageSize),
    isLoading,
    isError,
    nearMeActive,
    loadMoreHandler,
    canLoadMore: dynamicPartners?.length > pageSize * pageNumber,
    resultsRef,
    mapViewHandler,
    listViewHandler,
    listRef,
    showLocationDrawer,
    locationClickHandler,
    locationChangeHandler,
    selectedLocation,
    cancelLocationClickHandler,
  }
}
